import { useTranslation } from 'react-i18next';
import { generatePath, Link, useParams } from 'react-router-dom';
import { FaExternalLinkAlt } from 'react-icons/fa';

import { useGetCompaniesAtBuildingAddressQuery } from '@client/api';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@client/toolkit';

import { CompaniesPaths } from '../../../../../companies/companies.router';

/**
 * Shows a summary of companies in the tenant's DB whose BRREG business address matches
 * this building. Driven by GET /buildings/{id}/companies-at-address.
 */
export function CompaniesAtAddressCard() {
  const { t: tCompanies, ready } = useTranslation('companies');
  const { buildingId } = useParams<{ buildingId: string }>();

  const { data: companies, isFetching } = useGetCompaniesAtBuildingAddressQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" loading={!ready} variant="dense">
          Selskaper på denne adressen
        </CardTitle>
      </CardHeader>

      <CardContent>
        {isFetching ? (
          <div className="space-y-2">
            <Skeleton variant="text" className="w-[40%]" />
            <Skeleton variant="text" className="w-[30%]" />
          </div>
        ) : !companies || companies.length === 0 ? (
          <div className="text-sm text-white/50">
            Ingen registrerte selskaper med denne adressen i systemet.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-white/60 text-sm mb-2">
              {companies.length} {companies.length === 1 ? 'selskap' : 'selskaper'} registrert på denne
              adressen.
            </div>
            <div className="divide-y divide-border">
              {companies.map((company) => {
                const hasWarning =
                  company.isBankrupt || company.isUnderLiquidation || company.isUnderForcedDissolution;

                return (
                  <Link
                    key={company.id}
                    to={generatePath(CompaniesPaths.Company, { id: company.id })}
                    className="flex items-center justify-between py-2 hover:bg-surface-elevated px-2 -mx-2 rounded transition-colors group"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm font-medium truncate flex items-center gap-2">
                        {company.name ?? '—'}
                        <FaExternalLinkAlt className="text-white/30 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-white/40 text-xs">
                        {company.organizationNumber && `Org.nr. ${company.organizationNumber}`}
                        {company.industryCode && ` · ${company.industryCode}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {company.numberOfEmployees != null && (
                        <span className="text-xs text-white/50 tabular-nums">
                          {company.numberOfEmployees} ansatte
                        </span>
                      )}
                      {hasWarning && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                          {company.isBankrupt
                            ? tCompanies('form.isBankrupt')
                            : company.isUnderForcedDissolution
                            ? tCompanies('form.isUnderForcedDissolution')
                            : tCompanies('form.isUnderLiquidation')}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
